import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import { flushSync } from 'react-dom';
import api from '../../api/axiosInstance';
import './sideNavbar.css';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import PeopleIcon from '@mui/icons-material/People';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CampaignIcon from '@mui/icons-material/Campaign';
import SchoolIcon from '@mui/icons-material/School';
import DesignServicesIcon from '@mui/icons-material/DesignServices';

// ────────────────────────────────────────────
// NAV_SECTIONS — grouped navigation structure
// ────────────────────────────────────────────

const NAV_SECTIONS = [
  {
    section: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, excludedRoles: ['Viewer', 'Astern Admin'] },
      { label: 'Noticeboard', path: '/settings/noticeboard', icon: CampaignIcon, requiredRoleRights: ['NoticeBoard'] },
    ],
  },
  {
    section: 'Incentives',
    items: [
      {
        label: 'Incentives',
        icon: EmojiEventsIcon,
        excludedRoles: ['Viewer'],
        children: [
          { label: 'Campaigns', path: '/incentives', excludedRoles: ['Viewer'] },
          { label: 'Manage Campaigns', path: '/incentives/manage', requiredRoleRights: ['IncentiveManagement'] },
        ],
      },
    ],
  },
  {
    section: 'Sales',
    items: [
      {
        label: 'Sales',
        icon: ShoppingCartIcon,
        children: [
          { label: 'All Sales', path: '/sales', requiredRoleRights: ['AgentSalesList'] },
          { label: 'All Customer', path: '/sales/customers', requiredRoleRights: ['AgentSalesList'] },
          { label: 'Group Sales', path: '/sales/group', requiredRoleRights: ['GroupSalesList'] },
          { label: 'Create Sale', path: '/sales/create', requiredRoleRights: ['CreateSale'] },
        ],
      },
      {
        label: 'Commission',
        icon: MonetizationOnIcon,
        children: [
          { label: 'Personal PSC', path: '/commissions/psc', requiredRoleRights: ['PersonalSalesCommission'], availabilityKey: 'hasPsc' },
          { label: 'Downline DOC', path: '/commissions/doc', requiredRoleRights: ['DownlineOverridingCommission'], availabilityKey: 'hasDoc' },
          { label: 'Team TOC', path: '/commissions/toc', requiredRoleRights: ['TeamOverridingCommission'], availabilityKey: 'hasToc' },
          { label: 'Manager MOC', path: '/commissions/moc', requiredRoleRights: ['ManagerOverridingCommission'], availabilityKey: 'hasMoc' },
          { label: 'Referral Fee', path: '/commissions/referral', requiredRoleRights: ['PersonalSalesCommission'], availabilityKey: 'hasRef' },
          { label: 'Commission Slips', path: '/commissions/mySlips', requiredRoleRights: ['AgentCommissionSlips'] },
          { label: 'My Incentives', path: '/commissions/incentives', requiredRoleRights: ['PersonalSalesCommission'] },
        ],
      },
    ],
  },
  {
    section: 'Accounting',
    items: [
      {
        label: 'Accounting',
        icon: AssessmentIcon,
        children: [
          { label: 'All Sales', path: '/accounting/sales', requiredRoleRights: ['AllSalesList'] },
          { label: 'All Customer', path: '/accounting/customers', requiredRoleRights: ['AllSalesList'] },
          { label: 'Astern Claims', path: '/accounting/asternClaims', requiredRoleRights: ['TotalCommission'] },
          { label: 'All Sales Comm', path: '/accounting/salesCommissions', requiredRoleRights: ['AllSalesCommList'] },
          { label: 'Webbey Overriding', path: '/accounting/webbeyCommissions', requiredRoleRights: ['WebbeyCommission'] },
          { label: 'Slips', path: '/accounting/slips', requiredRoleRights: ['CommissionSlipGenerator'] },
          { label: 'CP58 (Tax)', path: '/accounting/cp58', requiredRoleRights: ['Cp58Generator'] },
        ],
      },
      {
        label: 'Approvals',
        icon: FactCheckIcon,
        children: [
          { label: 'Pending Approvals', path: '/approvals', requiredRoleRights: ['PendingApprovals'] },
        ],
      },
    ],
  },
  {
    section: 'Resources',
    items: [
      {
        label: 'Pricing & Tools',
        icon: CalculateIcon,
        excludedRoles: ['Astern Admin'],
        children: [
          { label: 'ATAP Pricing', path: '/resources/pricing' },
          { label: 'Add-on', path: '/resources/addons' },
          { label: 'RSC', path: '/tools/rsc' },
          { label: 'CSC', path: '/tools/csc' },
          { label: 'Design Request', path: '/design-requests', requiredRoleRights: ['DesignRequestAccess', 'CreateSale'] },
        ],
      },
      {
        label: 'Resources',
        icon: FolderIcon,
        excludedRoles: ['Astern Admin'],
        children: [
          { label: 'Tutorials', path: '/tutorials', icon: SchoolIcon },
          { label: 'Documents', path: '/resources/documents' },
          { label: 'FAQ', path: '/tools/faq' },
        ],
      },
    ],
  },
  {
    section: 'Reports',
    items: [
      {
        label: 'Reports',
        icon: SummarizeIcon,
        // Reports surface to earning roles + accountant/super admin.
        // Pure admin roles (Admin / Astern Admin / Group Admin) do NOT see
        // the Reports section at all — they're carved out deliberately so
        // the menu only shows up for people who actually have sales /
        // commissions / payouts to report on.
        requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'],
        children: [
          // Sales / Commission / Payout are scoped inside the page (Personal /
          // Group / All tabs based on the viewer's rights — see salesReport.jsx).
          { label: 'Sales Report',      path: '/reports/sales',      requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          { label: 'Commission Report', path: '/reports/commission', requiredRoles: ['Accountant', 'Super Admin', 'Leader', 'Sales Agent', 'In-house Agent'] },
          { label: 'Payout Report',     path: '/reports/payout',     requiredRoles: ['Accountant', 'Super Admin'] },
          // Webbey + Agent reports stay accountant/super-admin only — they're
          // org-wide views that don't make sense for an individual agent.
          { label: 'Webbey Report',     path: '/reports/webbey',     requiredRoles: ['Accountant', 'Super Admin'] },
          { label: 'Agent Report',      path: '/reports/agents',     requiredRoles: ['Accountant', 'Super Admin'] },
        ],
      },
    ],
  },
  {
    section: 'Admin',
    items: [
      {
        label: 'Users',
        icon: PeopleIcon,
        children: [
          { label: 'All User', path: '/admin/users', requiredRoleRights: ['UserManagement'] },
          // Agent Applications consolidated into /approvals -> "Agent Applications" tab (v1.5.24)
          { label: 'Agent Hierarchy', path: '/admin/hierarchy', requiredRoleRights: ['AgentHierarchy'] },
        ],
      },
      {
        label: 'Settings',
        icon: SettingsIcon,
        children: [
          { label: 'Profile', path: '/user/profile' },
          { label: 'Activity Logs', path: '/settings/logs', requiredRoleRights: ['UserManagement'] },
          // Permission Manager · Approver Config · Import Sales · Version Info now live
          // as tabs inside General Settings (Super Admin). Routes kept for direct access.
          { label: 'General Settings', path: '/settings/general', requiredRoles: ['Super Admin'] },
        ],
      },
    ],
  },
];

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

export const SideNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useUser();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openMenus, setOpenMenus] = useState({});
  const [activeDrawer, setActiveDrawer] = useState(null);

  const [deferredPrompt, setDeferredPrompt] = useState(window.__pwaInstallPrompt || null);

  const displayName = user?.firstName || user?.alias || '';
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];

  // Designer lockdown — true when the user carries ONLY the Designer role.
  // When true, every menu item except /design-requests is hidden.
  const isDesignerOnly =
    userRoles.includes('Designer') && userRoles.every(r => r === 'Designer');

  // Referral (and only Referral) sees the Commission menu only — no dashboard,
  // sales, hierarchy, resources, etc. Multi-role users keep their full access.
  const isReferralOnly =
    userRoles.includes('Referral') && userRoles.every(r => r === 'Referral');

  // Commission availability — hides PSC/DOC/TOC/MOC menu links when the
  // current user has no historical earnings of that type. Defaults all to
  // FALSE while loading: the strict-history rule means a single misleading
  // chip is worse than a brief flicker. Menu fills in once the API resolves.
  const [commAvail, setCommAvail] = useState({ hasPsc: false, hasDoc: false, hasToc: false, hasMoc: false, hasRef: false });
  useEffect(() => {
    if (!user?.userId) return;
    api.get('/CommissionProgress/GetMyCommAvailability')
      .then(r => setCommAvail(r.data || {}))
      .catch(() => { /* keep defaults; failure shouldn't break menu */ });
  }, [user?.userId]);

  // ---- PWA install prompt ----
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); window.__pwaInstallPrompt = e; setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    const prompt = deferredPrompt || window.__pwaInstallPrompt;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        setDeferredPrompt(null);
        window.__pwaInstallPrompt = null;
        if (outcome === 'accepted') {
          setTimeout(() => alert('App installed! Check your home screen.'), 300);
        }
      } catch (e) {
        console.log('Install prompt error:', e);
        showInstallGuide();
      }
    } else if (window.matchMedia('(display-mode: standalone)').matches) {
      alert('nexwave is already installed on this device.');
    } else {
      showInstallGuide();
    }
  };

  const showInstallGuide = () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (isIOS) {
      alert('To install:\n1. Tap the Share button (box with arrow)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add"');
    } else {
      alert('To install:\n1. Tap the browser menu (3 dots at top right)\n2. Tap "Add to Home screen" or "Install app"\n3. Tap "Install"');
    }
  };

  // ---- Responsive listener ----
  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- Access control ----
  // excludedRoles wins over everything else — used to hide otherwise-
  // ungated items from restricted roles like Viewer.
  const hasAccess = (item) => {
    if (item.excludedRoles?.length) {
      const blocked = item.excludedRoles.some(role => user?.roles?.includes(role));
      if (blocked) return false;
    }
    if (item.requiredRoles?.length) {
      const ok = item.requiredRoles.some(role => user?.roles?.includes(role));
      if (!ok) return false;
    }
    if (!item.requiredRoleRights?.length) return true;
    return item.requiredRoleRights.some(r => user?.roleRights?.includes(r));
  };

  // Hide menu links whose availabilityKey resolves false in commAvail.
  // Items without an availabilityKey aren't gated.
  const isAvailable = (item) => {
    if (!item.availabilityKey) return true;
    return !!commAvail[item.availabilityKey];
  };

  const filterChildren = (children) => {
    if (isDesignerOnly) {
      // Designer sees only the Design Request leaf; all other children are hidden.
      return children.filter(c => c.path === '/design-requests');
    }
    if (isReferralOnly) {
      // Referral earns only a referral fee — show just that link.
      return children.filter(c => c.path === '/commissions/referral' && hasAccess(c));
    }
    return children.filter(c => hasAccess(c) && isAvailable(c));
  };

  const hasVisibleChildren = (item) =>
    Array.isArray(item.children) && filterChildren(item.children).length > 0;

  const isItemVisible = (item) => {
    if (isDesignerOnly) {
      // For parent groups (with children), show the group only if it still has
      // a visible child after the Designer filter (i.e. Pricing & Tools only).
      if (item.children) return hasVisibleChildren(item);
      // For leaf items, hide everything — the only visible leaf lives inside
      // the Pricing & Tools group and is handled by filterChildren above.
      return false;
    }
    if (isReferralOnly) {
      // Only the Commission group (the one with /commissions children) shows.
      if (item.children) return hasVisibleChildren(item);
      return false;
    }
    if (item.children) return hasAccess(item) && hasVisibleChildren(item);
    return hasAccess(item);
  };

  const isSectionVisible = (section) =>
    section.items.some(isItemVisible);

  // Flatten all visible top-level items (for mobile tabs)
  const allVisibleItems = NAV_SECTIONS
    .filter(isSectionVisible)
    .flatMap(s => s.items.filter(isItemVisible));

  // ---- Menu toggle ----
  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // ---- Logout ----
  const handleLogout = () => {
    flushSync(() => {
      setUser(null);
      localStorage.clear();
    });
    navigate('/');
  };

  // ---- Active check helpers ----
  const isPathActive = (path) => location.pathname === path;

  const isParentActive = (item) => {
    if (!item.children) return isPathActive(item.path);
    return filterChildren(item.children).some(c => isPathActive(c.path));
  };

  // ---- User initials & photo ----
  const initials = displayName
    ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const profilePhotoUrl = user?.profilePhotoUrl;
  const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

  // ════════════════════════════════════════════
  //  DESKTOP SIDEBAR
  // ════════════════════════════════════════════

  const renderDesktop = () => (
    <aside className={`sidebar${isExpanded ? '' : ' collapsed'}`}>
      {/* Header */}
      <div className="sidebar-header" onClick={() => navigate(user?.roles?.length > 0 && user.roles.every(r => r === 'Viewer') ? '/user/profile' : isReferralOnly ? '/commissions/referral' : '/dashboard')} style={{ cursor: 'pointer' }}>
        <img src="/nexwaveLogo.png" alt="nexwave" className="sidebar-logo" />
        <div className="sidebar-brand">
          <span className="sidebar-brand-name">nexwave</span>
          <span className="sidebar-brand-sub">Sales CRM</span>
        </div>
      </div>

      {/* Nav Body */}
      <div className="sidebar-body">
        {NAV_SECTIONS.map((section) => {
          if (!isSectionVisible(section)) return null;

          return (
            <div className="sidebar-section" key={section.section}>
              <div className="sidebar-section-label">{section.section}</div>

              {section.items.map((item) => {
                if (!isItemVisible(item)) return null;
                const Icon = item.icon || DashboardIcon;
                const isOpen = !!openMenus[item.label];

                // Leaf item (no children)
                if (!item.children) {
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      className={`sidebar-nav-item${isPathActive(item.path) ? ' active' : ''}`}
                      title={!isExpanded ? item.label : undefined}
                    >
                      <span className="nav-icon"><Icon sx={{ fontSize: 20 }} /></span>
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  );
                }

                // Parent item with children
                return (
                  <div key={item.label}>
                    <button
                      className={`sidebar-nav-item${isParentActive(item) ? ' active' : ''}`}
                      onClick={() => {
                        if (!isExpanded) {
                          setIsExpanded(true);
                          setOpenMenus(prev => ({ ...prev, [item.label]: true }));
                        } else {
                          toggleMenu(item.label);
                        }
                      }}
                      title={!isExpanded ? item.label : undefined}
                    >
                      <span className="nav-icon"><Icon sx={{ fontSize: 20 }} /></span>
                      <span className="nav-label">{item.label}</span>
                      <span className={`nav-arrow${isOpen ? ' open' : ''}`}>
                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                      </span>
                    </button>

                    <div className={`sidebar-submenu${isOpen ? ' open' : ''}`}>
                      {filterChildren(item.children).map((child) => (
                        child.action === 'installApp' ? (
                          <a key="installApp" className="sidebar-submenu-link" style={{ cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleInstallApp(); }}>
                            {child.label}
                          </a>
                        ) : child.externalUrl ? (
                          <a
                            key={child.label}
                            href={child.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sidebar-submenu-link"
                          >
                            {child.label}
                          </a>
                        ) : (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`sidebar-submenu-link${isPathActive(child.path) ? ' active' : ''}`}
                          >
                            {child.label}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar" onClick={() => navigate('/user/profile')} style={{ cursor: 'pointer' }}>
            {profilePhotoUrl ? (
              <img src={`${apiBase}${profilePhotoUrl}`} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : initials}
          </div>
          <div className="sidebar-user-info">
            <p className="sidebar-username">{displayName}</p>
            <p className="sidebar-userrole" title={userRoles.join(', ')}>{userRoles.join(', ')}</p>
          </div>
          <button
            className="sidebar-logout-btn"
            onClick={async () => {
              if ('serviceWorker' in navigator) {
                try {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map(r => r.unregister()));
                } catch { /* ignore */ }
              }
              window.location.reload();
            }}
            title="Refresh"
            style={{ marginRight: 4 }}
          >
            <RefreshIcon sx={{ fontSize: 20 }} />
          </button>
          <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">
            <LogoutIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <span style={{ fontSize: '0.6rem', color: '#475569', fontFamily: "'Poppins', sans-serif", opacity: 0.6 }}>v1.2.0</span>
        </div>
      </div>

      {/* Collapse toggle */}
      <button className="sidebar-collapse-btn" onClick={() => setIsExpanded(prev => !prev)}>
        <ChevronLeftIcon
          className={`collapse-icon${isExpanded ? '' : ' rotated'}`}
          sx={{ fontSize: 20 }}
        />
      </button>
    </aside>
  );

  // ════════════════════════════════════════════
  //  MOBILE BOTTOM NAV
  // ════════════════════════════════════════════

  const renderMobile = () => {
    const primaryTabs = allVisibleItems.slice(0, 4);
    const overflowItems = allVisibleItems.slice(4);
    const hasOverflow = overflowItems.length > 0;

    // Which remaining sections have overflow items
    const overflowSections = NAV_SECTIONS
      .map(section => ({
        ...section,
        items: section.items.filter(
          item => isItemVisible(item) && overflowItems.includes(item),
        ),
      }))
      .filter(section => section.items.length > 0);

    const closeDrawer = () => setActiveDrawer(null);

    const handleTabClick = (item) => {
      if (item.children) {
        setActiveDrawer(prev => prev === item.label ? null : item.label);
      } else {
        closeDrawer();
        navigate(item.path);
      }
    };

    return (
      <>
        {/* Bottom tab bar */}
        <nav className="mobile-bottom-nav">
          {primaryTabs.map((item) => {
            const Icon = item.icon || DashboardIcon;
            const active = isParentActive(item);
            return (
              <button
                key={item.label}
                className={`mobile-tab${active ? ' active' : ''}`}
                onClick={() => handleTabClick(item)}
              >
                <Icon className="mobile-tab-icon" sx={{ fontSize: 24 }} />
                <span className="mobile-tab-label">{item.label}</span>
              </button>
            );
          })}
          {hasOverflow && (
            <button
              className={`mobile-tab${activeDrawer === '__more__' ? ' active' : ''}`}
              onClick={() => setActiveDrawer(prev => prev === '__more__' ? null : '__more__')}
            >
              <GridViewIcon className="mobile-tab-icon" sx={{ fontSize: 24 }} />
              <span className="mobile-tab-label">More</span>
            </button>
          )}
        </nav>

        {/* Drawer backdrop */}
        {activeDrawer && (
          <div className="mobile-drawer-backdrop" onClick={closeDrawer} />
        )}

        {/* Item drawer (submenu) */}
        {activeDrawer && activeDrawer !== '__more__' && (() => {
          const item = allVisibleItems.find(i => i.label === activeDrawer);
          if (!item || !item.children) return null;
          const Icon = item.icon || DashboardIcon;
          const children = filterChildren(item.children);

          return (
            <div className="mobile-drawer">
              <div className="mobile-drawer-header">
                <h3 className="mobile-drawer-title">{item.label}</h3>
                <button className="mobile-drawer-close" onClick={closeDrawer}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </button>
              </div>
              <div className="mobile-drawer-body">
                {children.map(child => (
                  child.action === 'installApp' ? (
                    <a key="installApp" className="mobile-drawer-link" style={{ cursor: 'pointer' }} onClick={async (e) => { e.preventDefault(); e.stopPropagation(); await handleInstallApp(); closeDrawer(); }}>
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </a>
                  ) : child.externalUrl ? (
                    <a
                      key={child.label}
                      href={child.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mobile-drawer-link"
                      onClick={closeDrawer}
                    >
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </a>
                  ) : (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`mobile-drawer-link${isPathActive(child.path) ? ' active' : ''}`}
                      onClick={closeDrawer}
                    >
                      <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                      {child.label}
                    </Link>
                  )
                ))}
              </div>
            </div>
          );
        })()}

        {/* More drawer */}
        {activeDrawer === '__more__' && (
          <div className="mobile-drawer">
            <div className="mobile-drawer-header">
              <h3 className="mobile-drawer-title">More</h3>
              <button className="mobile-drawer-close" onClick={closeDrawer}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
            <div className="mobile-drawer-body">
              {overflowSections.map(section => (
                <div className="more-drawer-section" key={section.section}>
                  <div className="more-drawer-section-label">{section.section}</div>
                  {section.items.map(item => {
                    const Icon = item.icon || DashboardIcon;

                    // Leaf item — same as before.
                    if (!item.children) {
                      return (
                        <Link
                          key={item.label}
                          to={item.path}
                          className={`mobile-drawer-link${isPathActive(item.path) ? ' active' : ''}`}
                          onClick={closeDrawer}
                        >
                          <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                          {item.label}
                        </Link>
                      );
                    }

                    // Parent item — render as expandable group, mirroring desktop sidebar.
                    // Children are no longer flattened directly under the SECTION header; they sit
                    // under the parent's label (e.g. "Pricing & Tools" / "Resources") so structure
                    // matches the desktop.
                    const isOpen = !!openMenus[item.label];
                    return (
                      <div key={item.label}>
                        <button
                          type="button"
                          className={`mobile-drawer-link mobile-drawer-parent${isParentActive(item) ? ' active' : ''}`}
                          onClick={() => toggleMenu(item.label)}
                        >
                          <Icon className="drawer-link-icon" sx={{ fontSize: 20 }} />
                          <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                          <ExpandMoreIcon
                            className={`mobile-drawer-parent-arrow${isOpen ? ' open' : ''}`}
                            sx={{ fontSize: 18 }}
                          />
                        </button>
                        <div className={`mobile-drawer-submenu${isOpen ? ' open' : ''}`}>
                          {filterChildren(item.children).map(child => (
                            child.externalUrl ? (
                              <a
                                key={child.label}
                                href={child.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-drawer-link mobile-drawer-child"
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </a>
                            ) : (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`mobile-drawer-link mobile-drawer-child${isPathActive(child.path) ? ' active' : ''}`}
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div className="more-drawer-divider" />

              {/* User info */}
              <div className="more-drawer-user">
                <div className="more-drawer-avatar" onClick={() => navigate('/user/profile')} style={{ cursor: 'pointer' }}>
                  {profilePhotoUrl ? (
                    <img src={`${apiBase}${profilePhotoUrl}`} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : initials}
                </div>
                <div>
                  <p className="more-drawer-username">{displayName}</p>
                  <p className="more-drawer-role">{userRoles.join(', ')}</p>
                </div>
              </div>

              <button className="more-drawer-logout" onClick={handleLogout}>
                <LogoutIcon sx={{ fontSize: 20 }} />
                Logout
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════

  return isMobile ? renderMobile() : renderDesktop();
};
